// Design-system components (design/v2/components), implemented with the
// token-based classes in styles/app.css.
import React, { useEffect, useRef, useState } from "react";
import { DUE_STATUS, dueText, initials as toInitials } from "../lib/format";

const cx = (...parts) => parts.filter(Boolean).join(" ");

export function Button({ variant = "outline", size, icon, iconOnly, block, className, children, type = "button", kbd, ...rest }) {
    return (
        <button
            type={type}
            className={cx("hz-btn", `hz-btn-${variant}`, size === "sm" && "hz-btn-sm", iconOnly && "hz-btn-icon", block && "hz-btn-block", className)}
            {...rest}
        >
            {icon && <i className={icon}></i>}
            {children}
            {kbd && <span className="hz-kbd-inline">{kbd}</span>}
        </button>
    );
}

export function Badge({ color = "secondary", soft = true, pill, children, className, title }) {
    return (
        <span title={title} className={cx("hz-badge", `hz-badge-${soft ? "soft-" : ""}${color}`, pill && "pill", className)}>
            {children}
        </span>
    );
}

// Tier badge: A/B/C, "custom" for a custom cadence only, or a dashed "None".
export function TierBadge({ tier, custom, showNone }) {
    if (tier) return <span className={`hz-tier hz-tier-${tier}`} title={`Tier ${tier}`}>{tier}</span>;
    if (custom) return <span className="hz-tier hz-tier-custom" title="Custom cadence">C*</span>;
    if (showNone) return <span className="hz-tier hz-tier-none" title="No tier">–</span>;
    return null;
}

// Due status badge for a contact/company object or a due-list row.
export function DueBadge({ status, dueInDays, snoozedUntil, withText }) {
    if (!status) return <span className="hz-muted hz-small">Not kept in touch</span>;
    const s = DUE_STATUS[status] || { label: status, color: "secondary" };
    return (
        <span className="hz-row" style={{ gap: 6 }}>
            <Badge color={s.color}>{s.label}</Badge>
            {snoozedUntil && <i className="ri-zzz-line hz-muted" title={`Snoozed until ${snoozedUntil}`}></i>}
            {withText && <span className="hz-small hz-muted">{dueText(dueInDays)}</span>}
        </span>
    );
}

export const dueDotColor = (status) => (status === "overdue" ? "danger" : status === "due_soon" ? "warning" : status === "ok" ? "success" : "secondary");

export function Dot({ color = "secondary", small }) {
    return <span className={cx("hz-dot", small && "hz-dot-sm", `hz-dot-${color}`)}></span>;
}

export function Avatar({ name, large }) {
    return <span className={cx("hz-avatar", large && "hz-avatar-lg")}>{toInitials(name)}</span>;
}

export function Card({ title, actions, children, className, bodyClass, noBody, footer, style }) {
    return (
        <div className={cx("hz-card", className)} style={style}>
            {(title || actions) && (
                <div className="hz-card-head">
                    <h2 className="hz-card-title">{title}</h2>
                    {actions && <div className="hz-row">{actions}</div>}
                </div>
            )}
            {noBody ? children : <div className={cx("hz-card-body", bodyClass)}>{children}</div>}
            {footer && <div className="hz-card-foot">{footer}</div>}
        </div>
    );
}

export function Spinner() {
    return <span className="hz-spinner" role="status" aria-label="Loading"></span>;
}

export function Loading({ text = "Loading…" }) {
    return (
        <div className="hz-loading">
            <Spinner /> {text}
        </div>
    );
}

export function Empty({ children, small }) {
    return <div className={small ? "hz-empty-sm" : "hz-empty"}>{children}</div>;
}

export function ErrorBox({ error, onRetry }) {
    if (!error) return null;
    return (
        <div className="hz-alert hz-alert-danger hz-row" style={{ margin: 16 }}>
            <i className="ri-error-warning-line"></i>
            <span className="hz-spacer">{error.message || String(error)}</span>
            {onRetry && <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>}
        </div>
    );
}

export function Field({ label, required, hint, children, style }) {
    return (
        <label className="hz-field" style={style}>
            {label && (
                <span className="hz-label">
                    {label}
                    {required && <span className="req"> *</span>}
                </span>
            )}
            {children}
            {hint && <div className="hz-hint">{hint}</div>}
        </label>
    );
}

export const Input = React.forwardRef(function Input({ className, small, ...rest }, ref) {
    return <input ref={ref} className={cx("hz-input", small && "hz-input-sm", className)} {...rest} />;
});

export function Textarea({ className, rows = 3, ...rest }) {
    return <textarea rows={rows} className={cx("hz-textarea", className)} {...rest} />;
}

// options: [{value, label}] or strings. value "" is the empty choice.
export function Select({ options = [], className, small, placeholder, ...rest }) {
    return (
        <select className={cx("hz-select", small && "hz-select-sm", className)} {...rest}>
            {placeholder !== undefined && <option value="">{placeholder}</option>}
            {options.map((o) =>
                typeof o === "string" ? (
                    <option key={o} value={o}>{o}</option>
                ) : (
                    <option key={o.value} value={o.value}>{o.label}</option>
                )
            )}
        </select>
    );
}

export function Checkbox({ checked, onChange, children, disabled }) {
    return (
        <label className="hz-check">
            <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
            {children}
        </label>
    );
}

export function Toggle({ on, onChange, children, disabled }) {
    return (
        <button type="button" disabled={disabled} className={cx("hz-toggle", on && "on")} onClick={() => onChange(!on)} aria-pressed={!!on}>
            <span className="hz-toggle-track"></span>
            {children}
        </button>
    );
}

export function SearchInput({ value, onChange, placeholder, autoFocus, style }) {
    return (
        <div className="hz-search" style={style}>
            <i className="ri-search-line"></i>
            <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} />
        </div>
    );
}

// tabs: [{key, label, count, danger, onRemove}]
export function Tabs({ tabs, active, onChange, style }) {
    return (
        <div className="hz-tabs" style={style}>
            {tabs.map((t) => (
                <div key={t.key} className="hz-row" style={{ gap: 0 }}>
                    <button type="button" className={cx("hz-tab", active === t.key && "active")} onClick={() => onChange(t.key)}>
                        {t.icon && <i className={t.icon}></i>}
                        {t.label}
                        {t.count !== undefined && t.count !== null && <span className={cx("hz-tab-count", t.danger && t.count > 0 && "danger")}>{t.count}</span>}
                    </button>
                    {t.onRemove && (
                        <button type="button" className="hz-tab-x" title="Remove view" onClick={t.onRemove}>
                            <i className="ri-close-line"></i>
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export function Segment({ options, value, onChange }) {
    return (
        <div className="hz-segment">
            {options.map((o) => (
                <button key={o.value} type="button" className={value === o.value ? "active" : ""} onClick={() => onChange(o.value)}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export function Chip({ active, onClick, icon, children, small }) {
    return (
        <button type="button" className={cx("hz-chip", active && "active", small && "hz-chip-sm")} onClick={onClick}>
            {icon && <i className={icon}></i>}
            {children}
        </button>
    );
}

export function Modal({ open, title, onClose, children, footer, width, bodyStyle }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose && onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);
    if (!open) return null;
    return (
        <div className="hz-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
            <div className="hz-modal" style={width ? { maxWidth: width } : undefined} role="dialog" aria-label={typeof title === "string" ? title : undefined}>
                <div className="hz-modal-head">
                    <h3 className="hz-modal-title">{title}</h3>
                    <button type="button" className="hz-modal-x" aria-label="Close" onClick={onClose}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>
                <div className="hz-modal-body" style={bodyStyle}>{children}</div>
                {footer && <div className="hz-modal-foot">{footer}</div>}
            </div>
        </div>
    );
}

// Confirmation dialog. Pass danger for destructive actions.
export function Confirm({ open, title, children, confirmLabel = "Confirm", danger, busy, onConfirm, onClose }) {
    return (
        <Modal
            open={open}
            title={title}
            onClose={onClose}
            width={440}
            footer={
                <>
                    <Button variant="light" onClick={onClose}>Cancel</Button>
                    <Button variant={danger ? "danger" : "primary"} disabled={busy} onClick={onConfirm}>
                        {busy ? <Spinner /> : null}
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            {children}
        </Modal>
    );
}

// Dropdown menu: trigger is a render prop receiving (open, toggle).
export function Menu({ trigger, children, align = "right" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDoc);
        window.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);
    return (
        <div className="hz-menu-wrap" ref={ref}>
            {trigger(open, () => setOpen((o) => !o))}
            {open && (
                <div className={cx("hz-menu", align === "left" && "left")} onClick={() => setOpen(false)}>
                    {children}
                </div>
            )}
        </div>
    );
}

export function MenuItem({ children, sub, onClick, danger }) {
    return (
        <button type="button" className={cx("hz-menu-item", danger && "danger")} onClick={onClick}>
            <span>{children}</span>
            {sub && <span className="hz-menu-sub">{sub}</span>}
        </button>
    );
}

export function PageHead({ crumb, title, sub, actions }) {
    return (
        <div className="hz-page-head">
            <div>
                {crumb && <div className="hz-crumb">{crumb}</div>}
                <h1 className="hz-page-title">{title}</h1>
                {sub && <div className="hz-page-sub">{sub}</div>}
            </div>
            {actions && <div className="hz-row-wrap">{actions}</div>}
        </div>
    );
}

export function Pager({ page, maxPage, count, onPage, label = "records" }) {
    if (!maxPage || maxPage <= 1) {
        return count ? <div className="hz-pager hz-small hz-muted">{count} {label}</div> : null;
    }
    return (
        <div className="hz-pager">
            <span className="hz-small hz-muted">
                {count} {label} · page {page} of {maxPage}
            </span>
            <div className="hz-row">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} icon="ri-arrow-left-s-line">Previous</Button>
                <Button size="sm" variant="outline" disabled={page >= maxPage} onClick={() => onPage(page + 1)}>Next <i className="ri-arrow-right-s-line"></i></Button>
            </div>
        </div>
    );
}

export function ScoreBar({ value, max = 100, color }) {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    return (
        <span className="hz-bar">
            <span className={`hz-fill-${color || "primary"}`} style={{ width: pct + "%" }}></span>
        </span>
    );
}

// Conic ring for a 0-100 value (qualification score).
export function Ring({ value, color = "primary" }) {
    if (value === null || value === undefined) return <span className="hz-ring hz-ring-empty">–</span>;
    const deg = Math.round((Math.max(0, Math.min(100, value)) / 100) * 360);
    return (
        <span className="hz-ring" style={{ background: `conic-gradient(var(--hz-${color}) ${deg}deg, var(--hz-slate-100) 0)` }}>
            <span className="hz-ring-inner">{value}</span>
        </span>
    );
}
